import { Avatar, Box, Button, Drawer, Stack, Typography } from '@mui/material'
import orderBy from 'lodash/orderBy'
import sortBy from 'lodash/sortBy'
import xor from 'lodash/xor'
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Swiper, SwiperClass, SwiperRef, SwiperSlide } from 'swiper/react'
import { IconButton, Icons, InformationModal } from 'utoniq-ui'

import {
  ItemThumbnailType,
  PersonalInformationType,
  PortfolioConnectionFragment,
  PortfolioSkuFragment,
  TicketItemMetadataAssignedNumberType,
  TicketItemMetadataThroughTicket,
  UserItemSerialDetailFragment,
} from '@/clients/utoniq-core/schema'
import {
  checkShowMessWaitAssignSeat,
  checkUserCanUseTicket,
  SwipeButton,
  TicketContainer,
  TicketNumberBox,
} from '@/components/organisms'
import { ItemReversibleThumbnail } from '@/components/organisms/item/ItemReversableThumbnail'
import { ThumbnailActionAreaCell } from '@/components/pages/OwnedItemDetail/template/components/ThumbnailActionArea'
import { styleSets } from '@/components/styles'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { CellFrame, Organism } from '@/core/architecture'
import { useAnalytics } from '@/lib/analytics'
import { dayjs } from '@/lib/dayjs'

import { State } from '../../../viewModel'
import { ItemDescriptionCell } from '../ItemDescription'
import { SupplementLinkSetCell } from '../SupplementLinkSet'

export type Props = {
  loggedUser: State['loggedUser']
  loading: boolean
  portfolio: State['portfolio']
  isFlipped: boolean
  onReverseThumbnail: () => void
  onClickProtectUserItem: () => void
  onUseTicket: (
    selectedSerials: string[],
    throughTicketName?: string
  ) => Promise<void>
  onOpenFullScreen?: () => void
  onOpenMultipleImage?: () => void
}

export const OwnedItemDetailTicketView: Organism<Props> = ({
  loading,
  portfolio,
  isFlipped,
  onReverseThumbnail,
  onClickProtectUserItem,
  onUseTicket,
  onOpenFullScreen,
  onOpenMultipleImage,
  loggedUser,
}) => {
  const [displayResource, setDisplayResource] = useState(false)
  const onClickDisplayResource = () => setDisplayResource(prev => !prev)
  const { t } = useTranslation('ownedItemDetail')
  const { routerPages, generatePath } = useRouter()
  const { analytics } = useAnalytics()

  const serialsFiltered = useMemo(() => {
    return portfolio?.serials?.filter(
      serial =>
        // case not share or user not accept item
        !serial.serial?.ticketDistributionLink?.user?.id ||
        // user not accept item and user accept is user login
        serial.serial.ticketDistributionLink.user.id === loggedUser?.uid
    )
  }, [portfolio?.serials, loggedUser?.uid])

  return (
    <CellFrame
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: '1 0 auto',
      }}
    >
      <Stack
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        flex={1}
      >
        <Stack alignItems="center">
          <Stack
            alignItems="center"
            sx={{
              position: 'relative',
              '.MuiButtonBase-root': {
                height: 'fit-content',
              },
            }}
          >
            <ThumbnailActionAreaCell
              onOpenFullScreen={onOpenFullScreen}
              onOpenMultipleImage={onOpenMultipleImage}
              numberImage={portfolio?.item.images?.length ?? 0}
            />
            <ItemReversibleThumbnail
              loading={loading}
              flipProps={{ isFlipped }}
              imageAreaProps={{
                thumbnail: portfolio?.item?.thumbnail,
                size:
                  portfolio?.item.thumbnailType === ItemThumbnailType.Square
                    ? 'x-large-square'
                    : 'x-large',
                onClick: onReverseThumbnail,
              }}
            />
          </Stack>
          <ItemDescriptionCell portfolio={portfolio} />
        </Stack>

        {/* イベントと関連づいている場合、受け取り一覧への導線を表示する。それ以外はチケット使用モーダルの導線を表示 */}
        {portfolio?.item?.collection?.event ? (
          <Box sx={{ mt: 2, mb: 2 }}>
            <Button
              variant="outlined"
              href={generatePath(routerPages.MyOwnedTickets, {
                eventId: portfolio?.item?.collection.event.id,
              })}
              onClick={() => {
                analytics?.track('click_eventPickupList', {
                  applicationKey: config.applicationKey,
                  type: 'ownedItem',
                })
              }}
            >
              {t('toPickup')}
            </Button>
          </Box>
        ) : (
          <>
            <TicketNumberBox
              loggedUser={loggedUser}
              item={portfolio?.item}
              serials={portfolio?.serials}
            />
            <Box sx={{ mt: 4, mb: 2 }}>
              <Button
                size="large"
                onClick={onClickDisplayResource}
                disabled={!serialsFiltered?.length}
              >
                {t('showTicketToUse')}
              </Button>
            </Box>
          </>
        )}
      </Stack>

      <TicketInfoDrawer
        open={displayResource}
        onClose={onClickDisplayResource}
        item={portfolio?.item}
        serials={serialsFiltered}
        onUseTicket={onUseTicket}
        user={portfolio?.user as PortfolioSkuFragment['user']}
      />

      <SupplementLinkSetCell
        sku={portfolio?.item.sku}
        itemId={portfolio?.item.id}
        commentCount={portfolio?.item.commentCount}
        relatedMissions={portfolio?.item.relatedMissions}
        artistId={portfolio?.item.issuedArtist?.id ?? ''}
        isUserProtected={portfolio?.isUserProtected}
        issuableNft={!!portfolio?.item.nft?.chains}
        onClickProtectUserItem={onClickProtectUserItem}
        isResaleAvailable={portfolio?.item?.isResaleAvailable}
        isResaled={!!portfolio?.item?.userOpenResaleItem}
      />
    </CellFrame>
  )
}

type TicketInfoDrawerProps = {
  open: boolean
  item?: PortfolioConnectionFragment['item'] & {
    qrCodeUri?: string
    multipleTicketNote?: string
  }
  onClose: () => void
  onUseTicket: Props['onUseTicket']
  serials?: UserItemSerialDetailFragment[]
  user?: PortfolioSkuFragment['user']
}
export const TicketInfoDrawer: Organism<TicketInfoDrawerProps> = ({
  open,
  item,
  serials,
  onClose,
  onUseTicket,
  user,
}) => {
  const { t } = useTranslation('ownedItemDetail')
  const { t: tCommon } = useTranslation()
  const [loading, setLoading] = useState<boolean>(false)
  const [isRotate, setIsRotate] = useState<boolean>(true)
  const [selectedSerials, setSelectedSerials] = useState<string[]>([])
  const [selectedThroughTicketName, setSelectedThroughTicketName] = useState<
    string | undefined
  >(undefined)
  const [throughTicketIndex, setThroughTicketIndex] = useState<number>(0)
  const [openConfirm, setOpenConfirm] = useState<boolean>(false)

  const { formattedUsedAt, disabled, unusedCount, succeed } = useMemo<{
    formattedUsedAt: null | string
    disabled: boolean
    unusedCount: number
    succeed: boolean
  }>(() => {
    if (!serials) {
      return {
        disabled: false,
        formattedUsedAt: null,
        unusedCount: 0,
        succeed: false,
      }
    }
    if (item?.metadata?.ticket?.isThroughTicket) {
      const unusedCount = serials.filter(
        serial =>
          serial.serial?.ticketStatus?.isExpired ||
          !serial.serial?.ticketStatus?.throughTicket?.[throughTicketIndex]
            ?.isUsed
      ).length

      const [lasted] = orderBy(
        serials.filter(
          serial =>
            serial.serial?.ticketStatus?.throughTicket?.[throughTicketIndex]
              ?.usedAt
        ),
        [`serial.ticketStatus.throughTicket[${throughTicketIndex}].usedAt`],
        ['desc']
      )
      const usedAllTicket = !unusedCount
      return {
        disabled: usedAllTicket,
        unusedCount,
        formattedUsedAt:
          lasted?.serial?.ticketStatus?.throughTicket?.[throughTicketIndex]
            ?.formattedUsedAt ?? null,
        succeed: usedAllTicket,
      }
    }
    if (serials.length === 1) {
      return {
        disabled: !serials?.[0]?.serial?.ticketStatus?.canUseTicket,
        unusedCount: 1,
        formattedUsedAt:
          serials?.[0]?.serial?.ticketStatus?.formattedUsedAt ?? null,
        succeed: !!serials?.[0]?.serial?.ticketStatus?.formattedUsedAt,
      }
    }
    const unusedCount = serials.filter(
      serial => !!serial.serial?.ticketStatus?.canUseTicket
    ).length
    const usedAllTicket = !unusedCount
    const [lasted] = orderBy(
      serials.filter(serial => serial?.serial?.ticketStatus?.usedAt),
      ['serial.ticketStatus.usedAt'],
      ['desc']
    )
    return {
      disabled: usedAllTicket,
      unusedCount,
      formattedUsedAt: lasted?.serial?.ticketStatus?.formattedUsedAt ?? null,
      succeed: usedAllTicket,
    }
  }, [item?.metadata?.ticket?.isThroughTicket, serials, throughTicketIndex])

  const showMessWaitAssignSeat = useMemo<boolean>(
    () =>
      checkShowMessWaitAssignSeat(
        item?.metadata?.ticket?.assignedNumberType,
        serials,
        throughTicketIndex
      ),
    [item?.metadata?.ticket?.assignedNumberType, serials, throughTicketIndex]
  )

  //set default selected ticket
  useEffect(() => {
    if (disabled || !serials?.length || !open || showMessWaitAssignSeat) {
      setSelectedSerials([])
      return
    }
    setSelectedSerials(
      serials
        .filter(serial =>
          checkUserCanUseTicket(
            serial.serial?.ticketStatus,
            item?.metadata?.ticket?.isThroughTicket
              ? throughTicketIndex
              : undefined
          )
        )
        .map(serial => serial.id)
    )
  }, [throughTicketIndex, open, showMessWaitAssignSeat])

  const fullName = useMemo(() => {
    // 転売防止のため、シリアル獲得時にuserからコピーされる氏名を優先して取得する
    // また、基本変わらない前提とし、一番若いシリアルに設定されている氏名を取得する
    const serialFullName = sortBy(serials, 'formattedSerialNumber').filter(
      v => !!v.serial?.metadata?.ticket?.fullName
    )[0]?.serial?.metadata?.ticket?.fullName

    // 下位互換のため、ない場合はuserの氏名を取得する
    return serialFullName ?? user?.fullName
  }, [serials, user?.fullName])

  const selectTicketLabel = useMemo<string>(() => {
    if (
      item?.metadata?.ticket?.assignedNumberType ===
      TicketItemMetadataAssignedNumberType.Seat
    ) {
      return t('seatNumberUse', '使用する座席番号')
    }
    return serials?.[0]?.serial?.formattedAssignedNumber
      ? t('assignedlNumberUse', '使用する整理番号')
      : t('serialNumberUse', '使用するシリアルナンバー')
  }, [item?.metadata?.ticket?.assignedNumberType, serials, t])

  const handleSelectSerial = useCallback((serialId: string) => {
    setSelectedSerials(prevState => xor(prevState, [serialId]))
  }, [])

  const handleClose = useCallback(() => {
    setSelectedSerials([])
    onClose()
    setThroughTicketIndex(0)
  }, [onClose])

  const handleRotate = () => {
    setIsRotate(e => !e)
  }

  const handleUseTicket = useCallback(async () => {
    setLoading(true)
    await onUseTicket(selectedSerials, selectedThroughTicketName).finally(
      () => {
        setLoading(false)
        setOpenConfirm(false)
        setSelectedSerials([])
      }
    )
  }, [onUseTicket, selectedSerials, selectedThroughTicketName])

  const getMembershipData = useCallback(() => {
    if (!user || !item?.issuedArtist?.id) return null
    const membershipData = user.organizationPatrons?.find(
      ({ artistId }) => artistId === item?.issuedArtist?.id
    )
    if (!membershipData?.artist?.subscription?.products) return null
    const { subscription: { products } = {} } = membershipData.artist
    const matchedProduct = products.find(product =>
      product.plans.some(
        ({ planId }) => planId === membershipData.subscription?.planId
      )
    )
    if (!matchedProduct) return null
    const membershipInterval = matchedProduct.plans.find(
      ({ planId }) => planId === membershipData.subscription?.planId
    )?.interval
    return {
      productTitle: matchedProduct.title,
      membershipInterval,
      continueMonth: membershipData.continueMonth ?? 0,
      membershipJoinedAt: membershipData.formattedCreatedAt,
    }
  }, [user, item])

  const ticket = item?.metadata?.ticket

  const ticketDate = useMemo(() => {
    const now = dayjs()
    const isThrough = ticket?.isThroughTicket
    const through = ticket?.throughTicket?.[throughTicketIndex]
    const rawStart = isThrough
      ? through?.startDateOfUse
      : ticket?.startDateOfUse
    const rawEnd = isThrough ? through?.endDateOfUse : ticket?.endDateOfUse
    const start = rawStart ? dayjs(rawStart) : null
    const end = rawEnd ? dayjs(rawEnd) : null
    if (!start && !end) return { enable: true, start, end }
    if (start && end) {
      return { enable: now.isAfter(start) && now.isBefore(end), start, end }
    }
    // 片方のみある場合、その当日中を判定
    const computedStart = start ?? end?.startOf('day')
    const computedEnd = end ?? start?.endOf('day')
    const enable =
      computedStart && computedEnd
        ? now.isSame(computedStart) ||
          (now.isAfter(computedStart) && now.isBefore(computedEnd))
        : false
    return {
      enable,
      start: computedStart,
      end: computedEnd,
    }
  }, [
    ticket?.isThroughTicket,
    ticket?.startDateOfUse,
    ticket?.endDateOfUse,
    ticket?.throughTicket,
    throughTicketIndex,
  ])

  return (
    <>
      <Drawer
        open={open}
        onClose={handleClose}
        sx={{ height: '95vh', zIndex: theme => theme.zIndex.modal + 2 }}
        PaperProps={{ sx: { height: '95vh' } }}
      >
        <Stack
          flex={1}
          sx={{
            height: '100%',
            bgcolor: 'background.default',
          }}
          overflow="unset"
        >
          <Stack
            flexDirection="row"
            alignItems="center"
            sx={{ px: 5, height: 64 }}
          >
            <IconButton.ScreenRotate
              edge="end"
              size="small"
              color="primary.main"
              onClick={handleRotate}
            />
            <Stack flex={1}>
              <Typography variant="h6" align="center">
                {t('ticketInfo')}
              </Typography>
            </Stack>
            <IconButton.Close edge="end" size="small" onClick={handleClose} />
          </Stack>
          <Stack overflow="hidden" flex={1}>
            <Stack
              overflow="scroll"
              flex={1}
              sx={{
                transform: isRotate ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.6s ',
              }}
            >
              <Stack
                flex={1}
                px={5}
                pt={4}
                spacing={6}
                sx={{
                  overflowY: 'auto',
                  pb: 4,
                  height: '100%',
                  ...styleSets.hideScrollbar(),
                }}
              >
                <Stack spacing={2}>
                  <Stack spacing={1}>
                    <Typography variant="h4">{item?.title}</Typography>
                    {!!item?.sku?.unitName && (
                      <Typography variant="h6">
                        {item?.sku?.unitName}
                      </Typography>
                    )}
                  </Stack>
                  {!!item?.metadata?.ticket?.description && (
                    <Typography variant="body2">
                      {item?.metadata?.ticket?.description}
                    </Typography>
                  )}
                </Stack>
                {item?.qrCodeUri && (
                  <Stack sx={{ pb: 1 }} alignItems="center">
                    <Avatar
                      src={item?.qrCodeUri}
                      variant="square"
                      sx={{ width: 120, height: 120 }}
                    />
                  </Stack>
                )}
                {/* ユーザー本名の取得がなっていれば、チケットスワイプ時に本名を表示する */}
                {(item?.personalInformationRequirements ?? []).includes(
                  PersonalInformationType.FullName
                ) && (
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="h6" color="text.secondary">
                        {t('ticketOwner')}
                      </Typography>
                    </Stack>
                    <Typography variant="h3">{fullName}</Typography>
                  </Stack>
                )}
                {getMembershipData() && (
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="h6" color="text.secondary">
                        {t('membership')}
                      </Typography>
                    </Stack>
                    <Typography variant="h6">
                      {`${t(
                        `membership_${getMembershipData()?.membershipInterval}`
                      )} ${tCommon('monthContinuation', {
                        month: getMembershipData()?.continueMonth,
                      })}`}
                    </Typography>
                    {getMembershipData()?.membershipJoinedAt && (
                      <Typography variant="h6">
                        {`${t('joinDate', {
                          date: getMembershipData()?.membershipJoinedAt,
                        })}`}
                      </Typography>
                    )}
                    <Typography variant="h6">
                      {getMembershipData()?.productTitle}
                    </Typography>
                  </Stack>
                )}
                {!!(ticketDate.start && ticketDate.end) && (
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="h6" color="text.secondary">
                        {t('ticketDateOfUse')}
                      </Typography>
                    </Stack>
                    <Typography variant="h6">
                      {t('ticketDateOfUseContent', {
                        start: ticketDate.start.format('YYYY/MM/DD HH:mm'),
                        end: ticketDate.end.format('YYYY/MM/DD HH:mm'),
                      })}
                    </Typography>
                  </Stack>
                )}
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="h6" color="text.secondary">
                      {selectTicketLabel}
                    </Typography>
                  </Stack>
                  {showMessWaitAssignSeat ? (
                    <Typography variant="body2">
                      {t(
                        'waitAssignYourSeat',
                        '座席番号が割り当てられるまでお待ち下さい。'
                      )}
                    </Typography>
                  ) : (
                    <>
                      {item?.metadata?.ticket?.throughTicket ? (
                        <TicketSelectSwiper
                          serials={serials}
                          throughTicketIndex={throughTicketIndex}
                          throughTickets={item.metadata.ticket.throughTicket}
                          selectedSerials={selectedSerials}
                          onSelectSerial={handleSelectSerial}
                          onChangeThroughTicketIndex={setThroughTicketIndex}
                          onSelectThroughTicketName={
                            setSelectedThroughTicketName
                          }
                        />
                      ) : (
                        <TicketContainer
                          serials={serials}
                          selectedSerials={selectedSerials}
                          onSelectSerial={handleSelectSerial}
                        />
                      )}
                    </>
                  )}
                </Stack>
              </Stack>

              {!showMessWaitAssignSeat && !!unusedCount && (
                <Stack sx={{ mt: 4 }}>
                  {!(formattedUsedAt && !selectedSerials.length) && (
                    <Typography variant="subtitle2" align="center">
                      {selectedSerials.length}/{unusedCount}
                      {t('selecting')}
                    </Typography>
                  )}
                </Stack>
              )}
              {!ticketDate.enable && (
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'error.main',
                    py: 2,
                    px: 4,
                    mt: 4,
                    mx: 5,
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    spacing={1}
                  >
                    <Icons.Error color="error" sx={{ fontSize: 20 }} />
                    <Typography variant="subtitle2">
                      {t('checkDate')}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="caption"
                    sx={{ display: 'inline-block', pt: 1, fontSize: '10px' }}
                  >
                    {t('checkDateDescription')}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    color="error.main"
                    textAlign="center"
                  >
                    {t('ticketStartDateOfUse')}
                    {ticketDate.start?.format('YYYY/MM/DD HH:mm') ?? '-'}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    color="error.main"
                    textAlign="center"
                  >
                    {t('ticketEndDateOfUse')}
                    {ticketDate.end?.format('YYYY/MM/DD HH:mm') ?? '-'}
                  </Typography>
                </Box>
              )}
              <Stack
                alignItems="center"
                sx={{
                  '&>.MuiBox-root:first-child': { width: '100%' },
                  py: 4,
                  px: 5,
                }}
              >
                <SwipeButton
                  onClick={async () => setOpenConfirm(true)}
                  size="large"
                  fullWidth
                  loading={loading}
                  disabled={disabled || !selectedSerials.length}
                  succeed={succeed || loading}
                  textOff={openConfirm ? '' : t('alreadyUsed')}
                  textOn={t('personCharge')}
                  openConfirm={openConfirm}
                  reverse={isRotate}
                  color={!ticketDate.enable ? 'error' : undefined}
                />
              </Stack>
              {formattedUsedAt && !selectedSerials.length && (
                <Stack
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                  spacing={1}
                  py={2}
                >
                  <Icons.Success fontSize="xSmall" />
                  <Typography variant="overline" color="text.secondary">
                    {t('dateOfUse')}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ pl: 1 }}
                  >
                    {formattedUsedAt}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Stack>

          {!showMessWaitAssignSeat && !!unusedCount && (
            <Stack spacing={2} sx={{ px: 5, py: 4, bgcolor: 'grey.50' }}>
              <Typography variant="caption">{t('personChargeNote')}</Typography>
            </Stack>
          )}
        </Stack>
      </Drawer>
      <Stack>
        <InformationModal
          open={openConfirm}
          onClose={() => {
            setOpenConfirm(loading ?? false)
          }}
          sx={{
            zIndex: theme => theme.zIndex.modal + 3,
            '#modal-inner': {
              transform: isRotate
                ? 'translate(-50%, -50%) rotate(180deg) '
                : 'translate(-50%, -50%)',
            },
          }}
          title={t('confirmChargeTitle')}
          content={t('confirmChargeDesc')}
          cancelText={loading ? '' : t('close')}
          buttons={
            <Button
              loading={loading}
              size="large"
              fullWidth
              onClick={handleUseTicket}
            >
              {t('ok')}
            </Button>
          }
        />
      </Stack>
    </>
  )
}

type TicketSelectSwiperProps = {
  throughTicketIndex: number
  serials?: UserItemSerialDetailFragment[]
  throughTickets: Pick<TicketItemMetadataThroughTicket, 'name'>[]
  selectedSerials: string[]
  onSelectSerial: (serialId: string) => void
  onChangeThroughTicketIndex: (throughTicketIndex: number) => void
  onSelectThroughTicketName: (name: string | undefined) => void
}
const TicketSelectSwiper: FC<TicketSelectSwiperProps> = ({
  throughTicketIndex,
  serials,
  throughTickets,
  selectedSerials,
  onSelectSerial,
  onChangeThroughTicketIndex,
  onSelectThroughTicketName,
}) => {
  const swiperRef = useRef<SwiperRef>(null)

  useEffect(() => {
    onSelectThroughTicketName(throughTickets[0]?.name)
    return () => onSelectThroughTicketName(throughTickets[0]?.name)
  }, [onSelectThroughTicketName, throughTickets])

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center">
        <IconButton.Prev
          onClick={() => swiperRef?.current?.swiper.slidePrev()}
          disabled={!!selectedSerials.length}
          bgcolor="grey.50"
          sx={{
            bgcolor: 'grey.50',
            visibility: throughTicketIndex === 0 ? 'hidden' : 'visible',
          }}
        />
        <Stack flex={1}>
          <Typography variant="subtitle1" width="100%" align="center">
            {throughTickets[throughTicketIndex]?.name}
          </Typography>
        </Stack>
        <IconButton.Next
          bgcolor="grey.50"
          onClick={() => swiperRef?.current?.swiper.slideNext()}
          disabled={!!selectedSerials.length}
          sx={{
            bgcolor: 'grey.50',
            visibility:
              throughTicketIndex + 1 === throughTickets.length
                ? 'hidden'
                : 'visible',
          }}
        />
      </Stack>

      <Swiper
        ref={swiperRef}
        loop={false}
        spaceBetween={20}
        allowTouchMove={false}
        enabled={throughTickets.length > 1}
        onActiveIndexChange={(swiper: SwiperClass) => {
          onChangeThroughTicketIndex(swiper.activeIndex)
          onSelectThroughTicketName(throughTickets[swiper.activeIndex]?.name)
        }}
      >
        {throughTickets.map((_, index) => (
          <SwiperSlide key={index} aria-disabled={!!selectedSerials.length}>
            <TicketContainer
              serials={serials}
              throughTicketIndex={index}
              selectedSerials={selectedSerials}
              onSelectSerial={onSelectSerial}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </Stack>
  )
}
